"use client";
import { useState, useEffect, useId } from "react";
import PropTypes from "prop-types";

import classNames from "classnames";

import { numberFormatter, priceFormatter } from "@/src/helpers/number-formatter";
import { createImageUrl } from "@/src/helpers/create-image-url";
import { highlighterQuickInfoLine } from "@/src/helpers/listing-card/quick-info-line";

import LocationIcon from "assets/svg/location_icon.svg";
import VisitVideo from "assets/svg/listingCard/visit_video.svg";
import View3d from "assets/svg/listingCard/view_3d.svg";

import BadgeCard from "components/common/badge-card";
import Price from "components/common/price";
import AddFavoriteButton from "../../common/add-favorite-button";
import ShowPhone from "components/common/show-phone/ShowPhone";
import { LazyImage } from "components/common/lazy-image/LazyImage";
import { currencyType } from "@/src/helpers/currency-type";
import similarListingSearchScrollGTMEvents from "helpers/google-tag-manager/scroll-bound/similar-listing-search";
import { GTMPushEvent } from "helpers/google-tag-manager";
import convertToUrl from "helpers/convert-to-url";
import { slugify } from "src/utils/string";

import styles from "./styles.module.css";

const ListingCard = ({
  badges = [],
  videoUrl = null,
  virtualTourUrl = null,
  title = "",
  locationSummary = "",
  categoryTypeName = "",
  estateTypeName = "",
  quickInfos = [],
  squareMeter = null,
  price = null,
  currency = "",
  previousPrice = null,
  previousCurrency = "",
  landFeatures = {},
  phoneNumber = "",
  coverPhoto = "",
  alternateBank = "",
  url = "",
  alternativeValue = "",
  id,
  listingItem,
  isPlatinum,
  index,
  similarCount,
  listings,
}) => {
  const keyId = useId();
  const virtualTour = virtualTourUrl;
  const category = categoryTypeName;
  const estateType = estateTypeName;
  const meterArea = (categoryTypeName === "Arsa" && quickInfos?.find((i) => i?.key === "square")) || squareMeter;
  const priceCurrency = currency;
  const previousPriceCurrency = previousCurrency;
  const meterPrice = categoryTypeName === "Arsa" && quickInfos?.find((i) => i?.key === "square_meter_price");
  const landFeature = landFeatures["Altyapı Özellikleri"];
  const featureVisibleCount = 2;
  const quickInfoList = highlighterQuickInfoLine(meterArea, estateType, quickInfos);
  const cityName = listingItem?.location?.city?.name;
  const districtName = listingItem?.location?.district?.name;
  const townName = listingItem?.location?.town?.name;
  const localityName = listingItem?.location?.locality?.name;
  const isFirstDisplayedCards = [0, 1, 2, 3].includes(index);

  const [isVisited, setIsVisited] = useState(false);

  useEffect(() => {
    const lastTwoSearchStorage = JSON.parse(localStorage.getItem("view-id-list")) || [];
    if (lastTwoSearchStorage?.includes(id)) {
      setIsVisited(true);
    }
  }, []);

  const priceClass = classNames({
    [styles.priceContent]: true,
    [styles.landMargin]: category === "Arsa",
  });

  const wrapperClass = classNames({
    [styles.wrapper]: true,
    [styles.wrapperIsPlatinum]: isPlatinum,
  });

  const imageClass = classNames({
    [styles.imageWrapper]: true,
    [styles.visitedImage]: isVisited,
  });

  const contentClass = classNames({
    [styles.contentWrapper]: true,
    [styles.visitedContent]: isVisited,
  });

  const cardIcon = classNames(isVisited && styles.visitedIcon);

  const renderBadges = () => {
    if (!badges || badges === 0) return null;
    let badgeList = [...badges];
    const isOpportunity = badges?.find((badge) => badge === "opportunity");
    if (isOpportunity && badges.length > 2) {
      badgeList = badges?.filter((badge) => badge === "promotion");
      badgeList.push("opportunity");
    }
    return badgeList.map((badgeType) => <BadgeCard key={badgeType} type={badgeType} />);
  };

  const imageUrl = createImageUrl(coverPhoto, {
    width: 500,
    height: 300,
  });

  const setLocalStorageView = () => {
    const lastTwoSearchStorage = JSON.parse(localStorage.getItem("view-id-list")) || [];
    if (!lastTwoSearchStorage?.includes(id)) {
      lastTwoSearchStorage.push(id);
      localStorage.setItem("view-id-list", JSON.stringify(lastTwoSearchStorage));
    }
  };

  const getVisibleContent = (features) => features.slice(0, featureVisibleCount).join(", ");

  const generateGa4EcommerceListingItem = (params) => {
    const { item, index, searchId, listName, affiliation } = params;
    const price = item?.priceDetail?.price || item?.price?.value;
    const currency = item.priceDetail?.currency || item?.price?.currency || item?.price_order;
    const title = convertToUrl(item.title.tr) || convertToUrl(item.title);
    const result = {
      search_id: searchId,
      item_price: item.price_order || price?.toFixed(2) || "0.00",
      item_id: `${item.id}`,
      item_name: title,
      currency,
      discount: 0.0,
      index,
      item_brand: "ilan",
      item_category: slugify(item.categoryTypeName) || slugify(item.category) || undefined,
      item_category2: slugify(item.estateTypeName) || slugify(item.estateType) || undefined,
      item_category3: slugify(item.location?.city?.name) || slugify(item.properties?.CATEGORY?.slug) || undefined,
      item_category4: slugify(item.location?.district?.name) || slugify(item.properties?.CITY?.slug) || undefined,
      item_category5: slugify(item.location?.town?.name) || slugify(item.properties?.TOWN?.slug) || undefined,
      item_list_id: "A7b9K1m4Qz8xL2wP0vN5rY6cJ3hD7tF1sV8u",
      item_list_name: listName,
      item_variant: slugify(item.tradeTypeName) || slugify(item.tradeType),
      location_id: item.location?.city?.id || item.location?.cityId || item.properties?.CITY?.id,
      price: 0.0,
      quantity: 1,
    };
    if (affiliation) {
      result.affiliation = affiliation;
    }
    return result;
  };

  const selectListingItemGA4Event = ({ item, index, searchId, listName, similarStatus }) => {
    GTMPushEvent({ ecommerce: null });
    GTMPushEvent({
      event: "select_item",
      ecommerce: {
        items: [
          generateGa4EcommerceListingItem({
            item,
            index,
            searchId,
            listName,
            similarStatus,
          }),
        ],
      },
    });
  };

  const handleGtmEvent = (listing, itemIndex) => {
    if (similarCount > 0) {
      const getSearchId = JSON.parse(localStorage.getItem("EjGtmId"));
      const searchId = getSearchId.listingsearch;
      selectListingItemGA4Event({
        item: listing,
        index: itemIndex + 1,
        listName: "ilan-listeleme/benzer-ilanlar",
        searchId,
        similarStatus: "true",
      });
    }
  };

  useEffect(() => {
    if (similarCount > 0) {
      const cleanup = similarListingSearchScrollGTMEvents({ listings });
      return cleanup;
    }
  }, [similarCount]);

  return (
    <div data-index={index + 1} data-id={listingItem.id} className={styles.container}>
      <a href={url} onClick={setLocalStorageView} className={wrapperClass}>
        <div className={imageClass} onClick={() => handleGtmEvent(listingItem, index)}>
          <div className={styles.badges}>{renderBadges()}</div>
          <div className={styles.favoriteIcon}>
            <AddFavoriteButton
              cityName={cityName}
              districtName={districtName}
              townName={townName}
              localityName={localityName}
              listingId={id}
            />
          </div>
          <LazyImage
            src={imageUrl}
            alt={`${categoryTypeName}-${estateTypeName}`}
            className={styles.imageClass}
            wrapperClassName={styles.imageWrapperClass}
            width="258px"
            height="100%"
            visibleByDefault={isFirstDisplayedCards}
          />
          {(videoUrl || virtualTour) && (
            <div className={styles.visitIcons}>
              {videoUrl && <VisitVideo className={cardIcon} />}
              {virtualTour && (
                <div className={videoUrl && virtualTour && styles.viewThree}>
                  <View3d className={cardIcon} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className={contentClass} onClick={() => handleGtmEvent(listingItem, index)}>
          <div className={styles.titleWrapper}>
            <h3 className={styles.title}>{title} </h3>
            {!!alternateBank && (
              <LazyImage
                src="/assets/images/alternatif_bank.png"
                alt={`${categoryTypeName}-${estateTypeName}`}
                className={styles.alternateLogo}
                width="62px"
                height="100%"
              />
            )}
          </div>
          <div className={styles.locationWrapper}>
            <LocationIcon className={cardIcon} />
            <span className={styles.location}>{locationSummary}</span>
          </div>
          <div className={styles.quickinfoWrapper}>
            {category === "Arsa" && estateType && meterArea ? (
              <div className={styles.landFeature}>
                {`${estateType} | ${meterArea?.value} `}
                {landFeature && ` | `}
                {landFeature && getVisibleContent(landFeature)}
                {landFeature?.length > featureVisibleCount && (
                  <div className={styles.featureCountWrapper}>
                    <span className={styles.featureCount}>
                      {` +`}
                      {landFeature?.length - featureVisibleCount}
                    </span>
                    <div className={styles.otherFeature}>
                      {landFeature?.map(
                        (item, index) =>
                          index >= featureVisibleCount && (
                            <div className={styles.otherFeatureItem} key={`${keyId}${index}`}>
                              {item}
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              quickInfoList
                ?.map((info) => {
                  if (info.key === "gross_square") {
                    const splittedGrossSquare = info?.value?.split(" ");
                    return `${numberFormatter(splittedGrossSquare[0])} ${splittedGrossSquare[1]}`;
                  }
                  return info.value;
                })
                .join(" | ")
            )}
          </div>
          <div className={priceClass}>
            <div>
              {category === "Arsa" && meterPrice && <div className={styles.meterPrice}> {meterPrice?.value}</div>}
              <div className={styles.priceWrapper}>
                {price ? (
                  <>
                    <span className={styles.price}>
                      <Price price={price} currency={priceCurrency} />
                    </span>
                    {previousPrice && (
                      <span className={styles.previousPrice}>
                        {priceFormatter(previousPrice)} {currencyType(previousPriceCurrency)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className={styles.price}>{alternativeValue}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </a>
      <div className={styles.buttonWrapper}>
        <ShowPhone
          listItemInfo={listingItem}
          phoneNumber={phoneNumber}
          gtmEventLabel="listeleme"
          hasGreenBackground={true}
        />
      </div>
    </div>
  );
};

ListingCard.propTypes = {
  badges: PropTypes.array,
  videoUrl: PropTypes.string,
  virtualTourUrl: PropTypes.string,
  title: PropTypes.string,
  locationSummary: PropTypes.string,
  categoryTypeName: PropTypes.string,
  estateTypeName: PropTypes.string,
  quickInfos: PropTypes.array,
  squareMeter: PropTypes.number,
  price: PropTypes.number,
  currency: PropTypes.string,
  previousPrice: PropTypes.number,
  previousCurrency: PropTypes.string,
  landFeatures: PropTypes.object,
  phoneNumber: PropTypes.string,
  coverPhoto: PropTypes.string,
  alternateBank: PropTypes.string,
  url: PropTypes.string,
  alternativeValue: PropTypes.string,
  id: PropTypes.number,
  listingItem: PropTypes.object,
  isPlatinum: PropTypes.bool,
  index: PropTypes.number,
  similarCount: PropTypes.number,
  listings: PropTypes.array,
};

export default ListingCard;
